import { IOrder, IRouteItem, ILocation, IOrderProduct } from "../types";
import { byStopNumber } from "../utils/comparators";

export interface CargoTotals {
  totalQuantity: number;
  /** Total weight, quantity-weighted. */
  totalWeight: number;
  /** Total volume in m³ (dimensions are cm), quantity-weighted. */
  totalVolume: number;
}

/**
 * Canonical cargo aggregation for an order's cargo lines.
 * Volume per unit = (length × width × height) / 1_000_000 (cm³ → m³); every
 * total is multiplied by the line quantity. Returns raw numbers; callers
 * decide on formatting/decimals.
 */
export const getCargoTotals = (
  cargo: IOrderProduct[] | undefined | null,
): CargoTotals => {
  let totalQuantity = 0;
  let totalWeight = 0;
  let totalVolume = 0;

  (cargo || []).forEach((item) => {
    const quantity = item.quantity || 0;
    const volumePerUnit =
      ((item.length || 0) * (item.width || 0) * (item.height || 0)) / 1000000;
    totalQuantity += quantity;
    totalWeight += (item.weight || 0) * quantity;
    totalVolume += volumePerUnit * quantity;
  });

  return { totalQuantity, totalWeight, totalVolume };
};

export interface OrderStops {
  pickups: IRouteItem[];
  deliveries: IRouteItem[];
}

/**
 * Canonical pickup/delivery stop resolution for an order's route.
 *
 * Tiered (preserves the most complete existing implementation):
 *  1. If any stop has the `isPickup` flag set, split by `isPickup === true/false`.
 *  2. Else if cargo carries itinerary stop numbers, split by
 *     `pickupStopNumber` / `deliveryStopNumber`.
 *  3. Else assume the first stop is the pickup and the rest are deliveries.
 *
 * The route is sorted by `stopNumber` first (non-mutating).
 */
export const getPickupAndDeliveryStops = (
  order: Pick<IOrder, "route" | "cargo">,
): OrderStops => {
  const route = order.route;
  if (!route || route.length === 0) return { pickups: [], deliveries: [] };

  const sortedRoute = [...route].sort(byStopNumber);

  // Tier 1: explicit isPickup flag
  const hasPickupFlag = sortedRoute.some((stop) => stop.isPickup !== undefined);
  if (hasPickupFlag) {
    return {
      pickups: sortedRoute.filter((stop) => stop.isPickup === true),
      deliveries: sortedRoute.filter((stop) => stop.isPickup === false),
    };
  }

  // Tier 2: cargo itinerary stop numbers
  if (order.cargo && order.cargo.length > 0) {
    const pickupStopNumbers = order.cargo
      .map((item) => item.itinerary?.pickupStopNumber)
      .filter((num) => num !== undefined);
    const deliveryStopNumbers = order.cargo
      .map((item) => item.itinerary?.deliveryStopNumber)
      .filter((num) => num !== undefined);

    return {
      pickups: sortedRoute.filter((stop) =>
        pickupStopNumbers.includes(stop.stopNumber),
      ),
      deliveries: sortedRoute.filter((stop) =>
        deliveryStopNumbers.includes(stop.stopNumber),
      ),
    };
  }

  // Tier 3: positional fallback
  return {
    pickups: sortedRoute.length > 0 ? [sortedRoute[0]] : [],
    deliveries: sortedRoute.length > 1 ? sortedRoute.slice(1) : [],
  };
};

/** First pickup stop's location for an order, or undefined. */
export const getPickupLocation = (
  order: Pick<IOrder, "route" | "cargo">,
): ILocation | undefined =>
  getPickupAndDeliveryStops(order).pickups[0]?.location;

/** First delivery stop's location for an order, or undefined. */
export const getDeliveryLocation = (
  order: Pick<IOrder, "route" | "cargo">,
): ILocation | undefined =>
  getPickupAndDeliveryStops(order).deliveries[0]?.location;
